import React from "react";
import { Image, Stack } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { t } from "i18next";

import "./DownloadAppPrompt.scss";
import BottomDrawer from "../BottomDrawer/BottomDrawer";
import {
  FunctionWithVoidReturn,
  GenericModalProps,
} from "../../utils/constants/generic-types";
import ImageHyperLogoWhite from "../../images/hyper-full-white.svg";
import ImageMedalGamepad from "../../images/medal-gamepad.svg";
import GetHyperOnTestFlightModal from "../GetHyperOnTestFlightModal/GetHyperOnTestFlightModal";
import useDisclosure from "../../hooks/useDisclosure";
import DownloadAndroidAppModal from "../DownloadAndroidAppModal/DownloadAndroidAppModal";
import GetAppButtonLoggedOut from "../GetAppButton/GetAppButtonLoggedOut";
import useNotifications from "../../hooks/useNotifications";
import NotificationFlow from "../notification-flow/NotificationFlow";
import SecondaryActionButton from "../Buttons/SecondaryActionButton/SecondaryActionButton";
import { DownloadAppOptionProperty } from "../../utils/analytics";
import useAnalytics from "../../hooks/useAnalytics";
import { useWindowDimensions } from "../../hooks/useWindowDimensions";
import CloseIcon from "../icons/CloseIcon";
import GetAppModal from "../GetAppModal";
import {
  useDialogMainText,
  useDialogSubText,
  PromptDownloadScenariosType,
  PROMPT_DOWNLOAD_SCENARIOS,
} from "./DownloadAppPrompt";
import { ROUTES } from "../../utils/constants";

interface DownloadAppPromptLoggedOutProps extends GenericModalProps {
  onOpen: FunctionWithVoidReturn;
  showNotificationsFlow?: boolean;
  promptDownloadScenario?: PromptDownloadScenariosType;
  className?: string;
  secondaryButtonText?: string;
  onSecondaryAction?: FunctionWithVoidReturn;
}

export default function DownloadAppPromptLoggedOut({
  isOpen,
  onOpen,
  onClose,
  showNotificationsFlow = true,
  promptDownloadScenario = PROMPT_DOWNLOAD_SCENARIOS.LOGGED_OUT.OTHERS,
  className = "",
  secondaryButtonText,
  onSecondaryAction,
}: DownloadAppPromptLoggedOutProps) {
  const {
    isOpen: isTestFlightInstructionsOpen,
    onOpen: openTestFlightInstructions,
    onClose: closeTestFlightInstructions,
  } = useDisclosure();
  const {
    isOpen: isAndroidAppDownloadOpen,
    onOpen: openDownloadAndroidApp,
    onClose: closeDownloadAndroidApp,
  } = useDisclosure();
  const {
    isOpen: isGetAppModalOpen,
    onOpen: openGetAppModal,
    onClose: closeGetAppModal,
  } = useDisclosure();
  const { notificationsAreEnabled, ...notifications } = useNotifications();
  const { DIALOG_MAIN_TEXT } = useDialogMainText();
  const { DIALOG_SUB_TEXT } = useDialogSubText();
  const { sendDownloadAppOptionsEvent } = useAnalytics();
  const { width: windowWidth } = useWindowDimensions();
  const isDesktop = windowWidth >= 1024;
  const navigate = useNavigate();

  const handleDownloadApp = (openPlatformDialog: () => void) => {
    onClose();
    openPlatformDialog();
  };

  const handleSecondaryAction = () => {
    if (onSecondaryAction) {
      sendDownloadAppOptionsEvent(DownloadAppOptionProperty.SKIP);
      onSecondaryAction();
      return;
    }
    sendDownloadAppOptionsEvent(DownloadAppOptionProperty.LOG_IN);
    navigate(ROUTES.ROOT.LOG_IN);
  };

  const handleClosePrompt = () => {
    sendDownloadAppOptionsEvent(DownloadAppOptionProperty.SKIP);
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClosePrompt();
    }
  };

  const getAndroidDownloadHandler = () => {
    return isDesktop ? openGetAppModal : openDownloadAndroidApp;
  };

  const getTestFlightHandler = () => {
    return isDesktop ? openGetAppModal : openTestFlightInstructions;
  };

  const promptContent = (
    <Stack gap={4} className="align-items-center DownloadAppDialog">
      <Image
        src={ImageHyperLogoWhite}
        alt="Hyper logo"
        width={119}
        height={20}
      />
      <Stack className="align-items-center TextWithGamepad">
        <Image
          src={ImageMedalGamepad}
          alt="Gamepad image"
          width={123}
          height={141.6}
        />
        <Stack gap={2} className="DialogText">
          <p className="MainText">{DIALOG_MAIN_TEXT[promptDownloadScenario]}</p>
          <p className="SubText">{DIALOG_SUB_TEXT[promptDownloadScenario]}</p>
        </Stack>
      </Stack>
      <Stack className="align-items-center Ctas">
        <GetAppButtonLoggedOut
          openDownloadAndroidApp={() =>
            handleDownloadApp(getAndroidDownloadHandler())
          }
          openTestFlightInstructions={() =>
            handleDownloadApp(getTestFlightHandler())
          }
          openDesktopModal={openGetAppModal}
        />
        <SecondaryActionButton
          text={secondaryButtonText || t("download_app_prompt.log_into_hyper")}
          onClick={handleSecondaryAction}
        />
      </Stack>
    </Stack>
  );

  return (
    <>
      {isOpen && isDesktop ? (
        <div
          className={`DownloadAppPromptModal ${className}`}
          onClick={handleBackdropClick}
        >
          <div className="ModalContent">
            <button
              className="CloseButton"
              onClick={handleClosePrompt}
              aria-label="Close"
            >
              <CloseIcon />
            </button>
            {promptContent}
          </div>
        </div>
      ) : (
        <BottomDrawer
          isOpen={isOpen}
          onClose={handleClosePrompt}
          containerClass={`${"DownloadAppDrawer"} ${className}`}
        >
          {promptContent}
        </BottomDrawer>
      )}

      <GetHyperOnTestFlightModal
        isOpen={isTestFlightInstructionsOpen}
        onClose={closeTestFlightInstructions}
      />
      <DownloadAndroidAppModal
        isOpen={isAndroidAppDownloadOpen}
        onClose={closeDownloadAndroidApp}
      />
      <GetAppModal isOpen={isGetAppModalOpen} onClose={closeGetAppModal} />
      {showNotificationsFlow && (
        <NotificationFlow handleGetApp={onOpen} {...notifications} />
      )}
    </>
  );
}
